"use client";

import { ChevronsUpDown } from "lucide-react";
import { useRouter } from "next/navigation";
import * as React from "react";
import { Gym, useGyms } from "@/api/gym";
import { Org, useOrgs } from "@/api/org";
import { Button } from "@/components/ui/button";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Skeleton } from "@/components/ui/skeleton";
import { useIsMobile } from "@/hooks/use-mobile";
import useActiveGym from "@/ui/hooks/active-gym";
import useActiveOrg from "@/ui/hooks/active-org";

interface ContextSwitcherProps {
  type: "orgs" | "gyms";
}

// Module-level cache that persists across component mounts
const moduleCache = {
  hasFetched: false,
  orgs: null as Org[] | null,
  gymsByOrg: {} as Record<string, Gym[]>,
};

// Skeleton loading for org/gym list
function ListSkeleton({ count = 5 }: { count?: number }) {
  return (
    <div className="p-2 space-y-2">
      {Array.from({ length: count }).map((_, i) => (
        <div key={i} className="flex flex-col gap-1">
          <Skeleton className="h-4 w-20" />
          <Skeleton className="h-8 w-full" />
        </div>
      ))}
    </div>
  );
}

// Component that fetches orgs and stores in module cache
function OrgsFetcher({ onOrgsLoaded }: { onOrgsLoaded: (orgs: Org[]) => void }) {
  const { data: orgs } = useOrgs(undefined, { revalidateOnFocus: false });

  React.useEffect(() => {
    if (orgs && !moduleCache.orgs) {
      moduleCache.orgs = orgs;
      onOrgsLoaded(orgs);
    }
  }, [orgs, onOrgsLoaded]);

  return null;
}

// Component to fetch gyms for an org and store in module cache
function GymFetcher({
  orgId,
  onGymsLoaded,
}: {
  orgId: string;
  onGymsLoaded: (orgId: string, gyms: Gym[]) => void;
}) {
  const { data: gyms } = useGyms({ org: orgId }, { revalidateOnFocus: false });

  React.useEffect(() => {
    if (gyms && !moduleCache.gymsByOrg[orgId]) {
      moduleCache.gymsByOrg[orgId] = gyms;
      onGymsLoaded(orgId, gyms);
    }
  }, [gyms, orgId, onGymsLoaded]);

  return null;
}

// Memoized component to render gym items for a specific org
const OrgGymsGroup = React.memo(function OrgGymsGroup({
  org,
  gyms,
  searchQuery,
  onSelect,
}: {
  org: Org;
  gyms: Gym[];
  searchQuery: string;
  onSelect: (value: { org: Org; gym: Gym | null; createInbox?: boolean }) => void;
}) {
  const query = searchQuery.toLowerCase();
  const orgNameMatches = !searchQuery || org.name.toLowerCase().includes(query);

  // Filter gyms by search query
  const filteredGyms = React.useMemo(() => {
    if (!searchQuery) return gyms;
    // If org name matches, show all gyms; otherwise filter by gym name
    if (orgNameMatches) return gyms;
    return gyms.filter((gym) => gym.name.toLowerCase().includes(query));
  }, [gyms, searchQuery, query, orgNameMatches]);

  // Don't render this group if searching and no matches
  if (searchQuery && filteredGyms.length === 0 && !orgNameMatches) {
    return null;
  }

  // When searching, hide orgs that have no gyms to display
  if (searchQuery && filteredGyms.length === 0) {
    return null;
  }

  return (
    <CommandGroup heading={org.name}>
      {filteredGyms.length > 0 ? (
        filteredGyms.map((gym) => (
          <CommandItem key={gym.id} value={gym.id} onSelect={() => onSelect({ org, gym })}>
            {gym.name}
          </CommandItem>
        ))
      ) : (
        <CommandItem
          key={`create-${org.id}`}
          value={`create-inbox-${org.id}`}
          onSelect={() => onSelect({ org, gym: null, createInbox: true })}
        >
          + Create first inbox
        </CommandItem>
      )}
    </CommandGroup>
  );
});

export function ContextSwitcher(props: ContextSwitcherProps) {
  const router = useRouter();
  const { org, setOrg } = useActiveOrg();
  const { gym, setGym } = useActiveGym();
  const [open, setOpen] = React.useState(false);
  const [searchQuery, setSearchQuery] = React.useState("");
  const isMobile = useIsMobile();

  // Local state to trigger re-renders when cache updates
  const [, forceUpdate] = React.useReducer((x) => x + 1, 0);

  // Track if we should fetch (set to true on first open, stays true forever)
  const [shouldFetch, setShouldFetch] = React.useState(moduleCache.hasFetched);

  // When popover opens for the first time ever, trigger fetch
  React.useEffect(() => {
    if (open && !moduleCache.hasFetched) {
      moduleCache.hasFetched = true;
      setShouldFetch(true);
    }
  }, [open]);

  // Callbacks to update local state when cache updates
  const onOrgsLoaded = React.useCallback(() => {
    forceUpdate();
  }, []);

  const onGymsLoaded = React.useCallback(() => {
    forceUpdate();
  }, []);

  // Read from module cache
  const cachedOrgs = moduleCache.orgs;
  const gymsByOrg = moduleCache.gymsByOrg;

  // Check if all gyms are loaded
  const allGymsLoaded =
    cachedOrgs && props.type === "gyms"
      ? cachedOrgs.every((o) => gymsByOrg[o.id] !== undefined)
      : true;

  // Determine loading state
  const isLoading = shouldFetch && (!cachedOrgs || (props.type === "gyms" && !allGymsLoaded));

  // For gyms mode: show all orgs (current org first)
  const sortedOrgs = React.useMemo(() => {
    if (!cachedOrgs) return [];
    const currentOrgId = org?.id;
    const currentOrg = currentOrgId ? cachedOrgs.find((o) => o.id === currentOrgId) : null;
    const otherOrgs = cachedOrgs.filter((o) => o.id !== currentOrgId);
    return currentOrg ? [currentOrg, ...otherOrgs] : otherOrgs;
  }, [cachedOrgs, org?.id]);

  // For orgs mode: filter by search query
  const filteredOrgs = React.useMemo(() => {
    const otherOrgs = cachedOrgs?.filter((o) => o.id !== org?.id) ?? [];
    if (!searchQuery) return otherOrgs;
    const query = searchQuery.toLowerCase();
    return otherOrgs.filter((o) => o.name.toLowerCase().includes(query));
  }, [cachedOrgs, org?.id, searchQuery]);

  // Memoized onSelect callback
  const onSelect = React.useCallback(
    (currentValues: { org: Org; gym: Gym | null; createInbox?: boolean }) => {
      if (currentValues.createInbox) {
        router.push("/settings/inboxes/new");
      }
      setOrg(currentValues.org);
      setGym(currentValues.gym || null);
      setOpen(false);
      setSearchQuery("");
    },
    [router, setOrg, setGym],
  );

  // Reset search when popover closes
  React.useEffect(() => {
    if (!open) {
      setSearchQuery("");
    }
  }, [open]);

  return (
    <>
      {/* Only render fetchers when shouldFetch is true and data not yet cached */}
      {shouldFetch && !moduleCache.orgs && <OrgsFetcher onOrgsLoaded={onOrgsLoaded} />}

      {/* Fetch gyms for all orgs once orgs are loaded */}
      {shouldFetch &&
        props.type === "gyms" &&
        cachedOrgs?.map(
          (orgItem) =>
            !gymsByOrg[orgItem.id] && (
              <GymFetcher key={orgItem.id} orgId={orgItem.id} onGymsLoaded={onGymsLoaded} />
            ),
        )}

      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Button
            variant="outline"
            size="sm"
            type="button"
            aria-expanded={open}
            className="w-full !bg-background justify-between !h-11 !border-border"
          >
            <span className="flex flex-col items-start justify-center w-full max-w-[calc(100%-24px)]">
              {props.type === "gyms" && (
                <span className="block text-[10px] text-muted-foreground leading-tight truncate max-w-full">
                  {org?.name}
                </span>
              )}
              <span className="block max-w-full leading-tight truncate">
                {props.type === "gyms" ? gym?.name : org?.name}
              </span>
            </span>
            <ChevronsUpDown className="shrink-0 w-4 h-4 ml-2 opacity-50" />
          </Button>
        </PopoverTrigger>
        <PopoverContent
          // On mobile the popover drops below a full-width trigger, so match
          // its width (Radix exposes it as a CSS var on the content).
          className={isMobile ? "w-[var(--radix-popover-trigger-width)] p-0" : "w-[280px] p-0"}
          side={isMobile ? "bottom" : "right"}
          align="start"
        >
          <Command shouldFilter={false}>
            <CommandInput
              placeholder="Search by name..."
              value={searchQuery}
              onValueChange={setSearchQuery}
            />
            <CommandList>
              {!shouldFetch || isLoading ? (
                <ListSkeleton count={4} />
              ) : (
                <>
                  <CommandEmpty>No results found.</CommandEmpty>
                  {props.type === "gyms" ? (
                    sortedOrgs.map((orgItem) => (
                      <OrgGymsGroup
                        key={orgItem.id}
                        org={orgItem}
                        gyms={gymsByOrg[orgItem.id] ?? []}
                        searchQuery={searchQuery}
                        onSelect={onSelect}
                      />
                    ))
                  ) : (
                    <CommandGroup>
                      {filteredOrgs.map((orgItem) => (
                        <CommandItem
                          key={orgItem.id}
                          value={orgItem.id}
                          onSelect={() => onSelect({ org: orgItem, gym: null })}
                        >
                          {orgItem.name}
                        </CommandItem>
                      ))}
                    </CommandGroup>
                  )}
                </>
              )}
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>
    </>
  );
}
